import { useState } from 'react';
import { useAppDispatch } from '../hooks/reduxHooks';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import { deleteProject } from '../ProjectSlice_Store';
import ChatModalFrame, { ChatModalFooter, ChatModalHeader } from './ChatModalFrame';

interface DeleteChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onDeleted: () => void;
}

export default function DeleteChatModal({ isOpen, onClose, projectId, projectName, onDeleted }: DeleteChatModalProps) {
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);

  useEscapeToClose(isOpen, onClose, isDeleting);

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteProject({ projectId })).unwrap();
      setIsDeleting(false);
      onDeleted();
    } catch {
      // Keep modal open on error so user can retry
      setIsDeleting(false);
    }
  };

  // Prevent closing while delete is in-flight
  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  return (
    <ChatModalFrame titleId="delete-chat-modal-title" onClose={handleClose}>
      <ChatModalHeader titleId="delete-chat-modal-title" title="Delete chat" />

      {/* Body */}
      <div className="px-6 py-4">
        <p className="text-sm text-gray-700">
          Are you sure you want to delete <span className="font-medium">{projectName}</span>?
        </p>
      </div>

      <ChatModalFooter
        onCancel={handleClose}
        onConfirm={() => void handleDelete()}
        isBusy={isDeleting}
        isConfirmDisabled={isDeleting}
        labels={{ confirm: 'Delete', busy: 'Deleting...' }}
      />
    </ChatModalFrame>
  );
}
